<template>
    <div class="layout-px-spacing">
        <teleport to="#breadcrumb">
            <ul class="navbar-nav flex-row">
                <li>
                    <div class="page-header">
                        <nav class="breadcrumb-one" aria-label="breadcrumb">
                            <ol class="breadcrumb">
                                <li class="breadcrumb-item active" aria-current="page">
                                    <span>View Violations</span>
                                </li>
                            </ol>
                        </nav>
                    </div>
                </li>
            </ul>
        </teleport>

        <div class="row layout-top-spacing">
            <div class="col-xl-12 col-lg-12 col-md-12 col-12 layout-spacing">
                <div class="panel br-4">
                    <div class="panel-body">
                        <template v-if="hasViewPermission">
                            <div class="d-flex justify-content-between align-items-center mb-3">
                                <h3 class="mb-0">
                                    Here are your available Violations so far! <br>
                                    <strong>You have {{ violationsList.length }} registered Violations.</strong>
                                </h3>
                                <router-link v-if="hasCreatePermission" to="/violation/add"
                                    class="btn btn-primary d-flex align-items-center">
                                    Add Violation
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                        fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                        stroke-linejoin="round" class="feather feather-plus ms-2">
                                        <line x1="12" y1="5" x2="12" y2="19"></line>
                                        <line x1="5" y1="12" x2="19" y2="12"></line>
                                    </svg>
                                </router-link>
                            </div>

                            <div class="table-responsive">
                                <div v-if="isLoading" class="text-center py-4">
                                    <div class="spinner-border text-primary" role="status">
                                        <span class="visually-hidden">Loading...</span>
                                    </div>
                                </div>

                                <template v-else>
                                    <table class="table table-hover table-bordered">
                                        <thead>
                                            <tr>
                                                <th>Name</th>
                                                <th>Description</th>
                                                <th>Date Created</th>
                                                <th class="text-center">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="violation in violationsList" :key="violation.id">
                                                <td>{{ violation.title }}</td>
                                                <td class="text-center">{{ violation.description }}</td>
                                                <td>{{ formatDate(violation.created_at) }}</td>
                                                <td class="text-center">
                                                    <!-- Edit Button -->
                                                    <button v-if="hasEditPermission" @click="goToEdit(violation.id)"
                                                        class="btn btn-primary btn-sm me-2">
                                                        Edit
                                                    </button>
                                                    <button v-if="hasDeletePermission"
                                                        @click="confirmDelete(violation.id)"
                                                        class="btn btn-danger btn-sm mt-1">
                                                        <i class="feather icon-trash-2"></i> Delete
                                                    </button>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                    <div v-if="violationsList.length === 0" class="text-center py-4">
                                        <p>No violations found</p>
                                    </div>
                                </template>
                            </div>
                        </template>
                        <div v-else class="alert alert-danger text-center">
                            <strong>Access Denied!</strong> You do not have permission to view this page.
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.layout-px-spacing {
    min-height: calc(100vh - 170px) !important;
}

.cursor-pointer {
    cursor: pointer;
}
</style>

<script setup>
import { ref, onMounted, computed } from "vue";
import { useRouter } from "vue-router";
import { useMeta } from "@/composables/use-meta";
import axios from 'axios';
import { BASE_URL } from '@/Config/config';
import Swal from 'sweetalert2';
import { useStore } from 'vuex';

useMeta({ title: "View Violations" });

const router = useRouter();
const store = useStore();

// Reactive Variables
const violationsList = ref([]);
const isLoading = ref(false);

// Permission Checks
const hasViewPermission = computed(() => store.getters.hasPermission('view_violation'));
const hasCreatePermission = computed(() => store.getters.hasPermission('create_violation'));
const hasEditPermission = computed(() => store.getters.hasPermission('edit_violation'));
const hasDeletePermission = computed(() => store.getters.hasPermission('delete_violation'));

const showErrorAlert = (message) => {
    Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: message,
        padding: '2em',
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'OK'
    });
};

const showSuccessAlert = (message) => {
    Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: message,
        padding: '2em',
        timer: 3000,
        timerProgressBar: true,
        showConfirmButton: false
    });
};

const fetchViolations = async () => {
    try {
        isLoading.value = true;
        const response = await axios.get(`${BASE_URL}/violations`, {
            headers: {
                'Authorization': `Bearer ${store.state.token}`,
                'X-Company-ID': store.state.currentCompany?.id
            }
        });
        violationsList.value = response.data;
    } catch (error) {
        console.error('Error fetching violations:', error);
        showErrorAlert(error.response?.data?.message || 'Failed to load violations');
    } finally {
        isLoading.value = false;
    }
};

onMounted(() => {
    if (hasViewPermission.value) {
        fetchViolations();
    }
});

const goToEdit = (id) => {
    if (hasEditPermission.value) {
        router.push(`/violation/edit/${id}`);
    } else {
        showErrorAlert("You don't have permission to edit violations");
    }
};

const confirmDelete = async (id) => {
    if (!hasDeletePermission.value) {
        showErrorAlert("You don't have permission to delete violations");
        return;
    } else {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Delete',
            padding: '2em'
        });

        if (result.isConfirmed) {
            try {
                await axios.delete(`${BASE_URL}/violations/${id}`, {
                    headers: {
                        'Authorization': `Bearer ${store.state.token}`,
                        'X-Company-ID': store.state.currentCompany?.id
                    }
                });
                violationsList.value = violationsList.value.filter(v => v.id !== id);
                showSuccessAlert('Violation has been deleted');
            } catch (error) {
                console.error('Error deleting violation:', error);
                showErrorAlert(error.response?.data?.message || 'Failed to delete violation');
            }
        }
    }
};

const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
};
</script>